import { useEffect } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";

export default function Privacy() {
    useEffect(() => {
        document.title = "Surveys2CAD | Privacy Policy";
    }, []);

    const lastUpdated = "February 18, 2026";

    return (
        <div data-testid="privacy-page">
            <PageHero variant="about" tint="cyan">
                <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 md:py-20">
                    <div className="overline mb-3">Legal</div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-slate-400 mt-4 text-sm font-mono-alt">Last updated: {lastUpdated}</p>
                </div>
            </PageHero>

            <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 md:py-20">
                <div className="prose prose-invert prose-slate max-w-none space-y-6 text-slate-300 leading-relaxed text-[15px]">
                    <p>
                        Surveys2CAD ("<b>we</b>", "<b>us</b>", "<b>our</b>") respects your privacy. This Privacy Policy explains
                        how we collect, use, disclose, and safeguard your information when you visit
                        <b> surveys2cad.com</b> or use any of our land surveying CAD drafting services.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">1. Information We Collect</h2>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li><b>Contact info</b> you submit through forms — name, email, phone, company, state, and project details.</li>
                        <li><b>Project files</b> you upload for drafting — AutoCAD, RAW survey data, PDFs, point clouds.</li>
                        <li><b>Usage data</b> — pages visited, referrer, browser, device, approximate location (via IP).</li>
                        <li><b>Cookies</b> used only for site functionality (session, preferences). We do not use third-party advertising cookies.</li>
                    </ul>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">2. How We Use Your Information</h2>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li>Respond to quote requests and deliver the drafting services you commission.</li>
                        <li>Send project updates, invoices, and — if you opt-in — our monthly newsletter.</li>
                        <li>Improve our website, services, and customer experience.</li>
                        <li>Comply with legal obligations (tax, accounting, litigation holds).</li>
                    </ul>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">3. How We Share Information</h2>
                    <p>
                        We <b>do not sell</b> your personal information. We share data only with:
                    </p>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li>Vetted sub-contractor drafters (under NDA) strictly to fulfill your project.</li>
                        <li>Infrastructure providers (MongoDB, Gmail SMTP, hosting) — bound by their standard data-processing terms.</li>
                        <li>Law enforcement when compelled by valid legal process.</li>
                    </ul>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">4. Data Retention</h2>
                    <p>
                        Project files are retained for 12 months after delivery so you can re-request them, then securely deleted.
                        Contact records are retained until you request deletion (see §7). Email campaign unsubscribe records are
                        retained permanently to honor your opt-out.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">5. Data Security</h2>
                    <p>
                        We protect your data with HTTPS/TLS in transit, encrypted storage at rest, bcrypt-hashed credentials,
                        httpOnly session cookies, and role-based admin access. No method is 100% secure, but we review our
                        controls regularly.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">6. Third-Party Services</h2>
                    <p>
                        Our site uses: Gmail SMTP (transactional + campaign email), MongoDB Atlas (storage), and our hosting
                        provider. We do <b>not</b> use Google Analytics, Facebook Pixel, or advertising trackers.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">7. Your Rights (GDPR / CCPA)</h2>
                    <p>You may at any time request to:</p>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li>Access a copy of the personal data we hold about you.</li>
                        <li>Correct inaccurate data.</li>
                        <li>Delete your data (subject to legal retention obligations).</li>
                        <li>Unsubscribe from marketing emails (one-click link in every message).</li>
                        <li>Opt out of the sale of personal information (we don't sell it, but CCPA requires us to say so).</li>
                    </ul>
                    <p>
                        Email <a className="text-cyan-300 hover:text-cyan-200" href="mailto:surveys2cad@gmail.com">surveys2cad@gmail.com</a> to exercise any right.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">8. Children's Privacy</h2>
                    <p>Our services are not directed to anyone under 18. We do not knowingly collect data from minors.</p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">9. Changes to This Policy</h2>
                    <p>
                        We may update this policy occasionally. The "Last updated" date at the top reflects the most recent
                        revision. Material changes will be announced on this page and — for active clients — by email.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">10. Contact</h2>
                    <p>
                        Questions? Email <a className="text-cyan-300 hover:text-cyan-200" href="mailto:surveys2cad@gmail.com">surveys2cad@gmail.com</a>
                        {" "}or reach out via our <Link to="/contact" className="text-cyan-300 hover:text-cyan-200">contact page</Link>.
                    </p>
                </div>
            </section>
        </div>
    );
}
